var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DuePresetPicker } from 'components/activity/DuePresetPicker';
import { Button } from 'components/Button';
import { classNames, React, useLayoutEffect, useRef, useState } from 'libs/react';
import './ActivityComposer.scss';
/**
 * @component ActivityComposer
 * @since 1.0.0
 * Writing field for notes and reminders. A single quiet line at rest, it
 * grows and reveals its actions once focused.
 */
export function ActivityComposer(props) {
    const { placeholder, submitLabel, withDueDate = false, onCancel, defaultContent = '', defaultDueAt = null } = props;
    const editing = !!onCancel;
    const ref = useRef(null);
    const [content, setContent] = useState(defaultContent);
    const [dueAt, setDueAt] = useState(defaultDueAt);
    const [focused, setFocused] = useState(editing);
    const [processing, setProcessing] = useState(false);
    const open = editing || focused || !!content || !!dueAt;
    const ready = !!content.trim() && (!withDueDate || !!dueAt);
    // Grow with the text, up to the CSS max-height
    useLayoutEffect(() => {
        const node = ref.current;
        if (node) {
            node.style.height = 'auto';
            node.style.height = node.scrollHeight + 'px';
        }
    }, [content, open]);
    useLayoutEffect(() => {
        const node = ref.current;
        if (editing && node) {
            node.focus();
            node.setSelectionRange(node.value.length, node.value.length);
        }
    }, []);
    /**
     * @function submit
     * @since 1.0.0
     */
    function submit() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!ready || processing)
                return;
            setProcessing(true);
            try {
                yield props.onSubmit(content.trim(), dueAt);
                if (!editing) {
                    setContent('');
                    setDueAt(null);
                    setFocused(false);
                    (_a = ref.current) === null || _a === void 0 ? void 0 : _a.blur();
                }
            }
            catch (_b) {
                // The caller reports the error; keep the text so nothing is lost
            }
            finally {
                setProcessing(false);
            }
        });
    }
    /**
     * @function onKeyDown
     * @since 1.0.0
     */
    function onKeyDown(e) {
        var _a;
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
            return;
        }
        if (e.key === 'Escape') {
            if (editing) {
                onCancel === null || onCancel === void 0 ? void 0 : onCancel();
            }
            else if (!content) {
                setDueAt(null);
                (_a = ref.current) === null || _a === void 0 ? void 0 : _a.blur();
            }
        }
    }
    return (React.createElement("div", { className: classNames('activity-composer', props.className, {
            'activity-composer--open': open,
            'activity-composer--editing': editing
        }), onFocus: () => setFocused(true), onBlur: (e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
                setFocused(false);
            }
        } },
        React.createElement("textarea", { ref: ref, className: "activity-composer-input", value: content, placeholder: placeholder, rows: 1, onChange: (e) => setContent(e.target.value), onKeyDown: onKeyDown }),
        open && (React.createElement("div", { className: "activity-composer-footer" },
            withDueDate && (React.createElement(DuePresetPicker, { className: "activity-composer-due", value: dueAt, onChange: setDueAt })),
            React.createElement("div", { className: "activity-composer-actions" },
                React.createElement("span", { className: "activity-composer-hint" },
                    navigator.platform.includes('Mac') ? '⌘' : 'Ctrl',
                    " + \u21B5"),
                onCancel && (React.createElement(Button, { label: t('Cancel'), style: "outline", size: "small", onPress: onCancel })),
                React.createElement(Button, { label: submitLabel, style: "primary", size: "small", processing: processing, disabled: !ready, onPress: submit }))))));
}
